import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { EavAttributeFindInterface } from '@zaoblako/shared/modules/eav/dtos/attribute/eav-attribute-find.dto';
import type { EavEntityTypeFindInterface } from '@zaoblako/shared/modules/eav/dtos/entity-type/eav-entity-type-find.dto';
import {
  EavEntityTypeFormDto,
  type EavEntityTypeFormInterface
} from '@zaoblako/shared/modules/eav/dtos/entity-type/eav-entity-type-form.dto';
import { EavAttributeValueTypeEnum } from '@zaoblako/shared/modules/eav/enums/eav-attribute-value-type.enum';
import { HelpdeskTaskStatusFindInterface } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-task-status-find.dto';
import { CrmTableColumn } from '@zaoblako/web/core/models/crm-table-column.interface';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { HelpDeskEavEntityTypeService } from '@zaoblako/web/core/services/helpdesk/help-desk-eav-entity-type.service';
import { HelpDeskTaskService } from '@zaoblako/web/core/services/helpdesk/help-desk-task.service';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { MultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/multi-select/multi-select.component';
import { SingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/single-select/single-select.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { TableComponent } from '@zaoblako/web/shared/dumbs/table/table.component';
import { BalanceReportIncomePercentModeNamePipe } from '@zaoblako/web/shared/pipes/balance-report-income-percent-mode-name.pipe';
import { EavAttributeValueTypeNamePipe } from '@zaoblako/web/shared/pipes/eav-attribute-value-type-name.pipe';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { SelectButtonModule } from 'primeng/selectbutton';
import { Observable } from 'rxjs';

@Component({
  standalone: true,
  selector: 'crm-helpdesk-eav-entity-type-form',
  templateUrl: './helpdesk-eav-entity-type-form.component.html',
  imports: [
    CommonModule,
    FormsModule,
    SpinnerComponent,
    JoinPipe,
    ButtonModule,
    CardModule,
    InputTextComponent,
    SelectButtonModule,
    TableComponent,
    SingleSelectComponent,
    MultiSelectComponent,
    BalanceReportIncomePercentModeNamePipe,
    EavAttributeValueTypeNamePipe,
    CheckboxModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HelpdeskEavEntityTypeFormComponent
  extends BaseFormComponent<EavEntityTypeFormInterface>
  implements OnInit
{
  @Input() public readonly index: number;
  public dto = EavEntityTypeFormDto;
  public engine: 'class-transformer' | 'serde' = 'serde';
  public type: EavEntityTypeFindInterface = <EavEntityTypeFindInterface>{};

  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public attributeColumns: CrmTableColumn[] = [
    { label: 'Название', class: 'w-3' },
    { label: 'Тип поля', class: 'w-3' },
    { label: 'Обязательно в статусах', class: 'w-3' },
    { class: 'w-2' },
    { class: 'w-1' }
  ];

  public attributeStatuses: HelpdeskTaskStatusFindInterface[] = [];

  public attributeTypes: EavAttributeValueTypeEnum[] = Object.values(EavAttributeValueTypeEnum);

  public attributeErrors: string[] = [];

  public get EavAttributeValueTypeEnum() {
    return EavAttributeValueTypeEnum;
  }

  public constructor(
    private readonly helpDeskEavEntityTypeService: HelpDeskEavEntityTypeService,
    private readonly helpDeskTaskService: HelpDeskTaskService,
    private readonly confirmService: ConfirmDialogService,
    private readonly formStoreService: FormStoreService,
    private readonly errorService: ErrorService,
    private readonly cdRef: ChangeDetectorRef
  ) {
    super();
  }

  public ngOnInit(): void {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    this.helpDeskTaskService.getStatuses().subscribe((statuses) => (this.attributeStatuses = statuses));

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    }
  }

  public getById(): void {
    this.formStoreService.setLoading(true, this.index);

    this.helpDeskEavEntityTypeService
      .findById<EavEntityTypeFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId)
      .subscribe({
        next: (type) => {
          this.type = type;
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
  }

  public saveEavEntityType(): void {
    this.attributeErrors = [];

    if (!this.type.attributes?.length) {
      this.attributeErrors.push('Список не может быть пустым');
      return;
    }

    this.type.attributes?.forEach((attribute, index) => {
      if (!attribute.name) {
        this.attributeErrors.push(`${index + 1} строчка: Название не указано`);
      }
      if (!attribute.type) {
        this.attributeErrors.push(`${index + 1} строчка: Тип поля не указан`);
      }
    });

    if (this.checkValidate(this.type).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.helpDeskEavEntityTypeService
          .update<EavEntityTypeFormInterface, EavEntityTypeFindInterface>(this.type)
          .subscribe({
            next: (type) => {
              this.formStoreService.setLoading(false, this.index);
              this.errorService.addSuccessMessage(`Шаблон полей "${this.type.name}" обновлен!`);
              this.formStoreService.saveNext(type, this.index);
            },
            error: () => {
              this.formStoreService.setLoading(false, this.index);
            }
          });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.helpDeskEavEntityTypeService.create<EavEntityTypeFindInterface>(this.type).subscribe({
          next: (type) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Шаблон полей создан!');
            this.formStoreService.saveNext(type, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteEavEntityType(): void {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить шаблон полей "${this.type.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.helpDeskEavEntityTypeService
          .deleteById(this.formStoreService.getState.forms[this.index]?.entityId)
          .subscribe({
            next: () => {
              this.errorService.addSuccessMessage(`Шаблон полей "${this.type.name}" удален!`);
              this.formStoreService.deleteNext(this.index);
            },
            error: () => {
              this.formStoreService.setLoading(false, this.index);
            }
          });
      }
    });
  }

  public restoreEavEntityType(): void {
    this.confirmService.confirm({
      message: `Вы действительно хотите восстановить шаблон полей "${this.type.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.helpDeskEavEntityTypeService
          .restoreOne(this.formStoreService.getState.forms[this.index]?.entityId)
          .subscribe({
            next: () => {
              this.type = { ...this.type, deleted: null };
              this.cdRef.markForCheck();
              this.errorService.addSuccessMessage(`Шаблон полей "${this.type.name}" восстановлен!`);
              this.formStoreService.saveNext(this.type, this.index);
            },
            error: () => {
              this.formStoreService.setLoading(false, this.index);
            }
          });
      }
    });
  }

  public toAttribute(attribute: any): EavAttributeFindInterface {
    return attribute as EavAttributeFindInterface;
  }

  public addAttribute(): void {
    if (!Array.isArray(this.type.attributes)) {
      this.type.attributes = [];
    }

    this.type.attributes = this.type.attributes.concat(<EavAttributeFindInterface>{});
  }

  public deleteAttribute(attribute: EavAttributeFindInterface): void {
    this.type.attributes = this.type.attributes.filter((a) => a !== attribute);
  }
}
